import { CalendarDots, CirclesFour, GearSix, ListBullets, Notepad, UserIcon, VideoCamera } from "@phosphor-icons/react";
import React from "react";
import { useNavigate } from "react-router";
import { ENUM_USER_ROLE } from "../../../enums";
import { ROUTE_PATHS } from "../../../routes/paths";
import { useAppSelector } from "../../../store";
import { ButtonMotion } from "../../form/buttons";
import { appToast } from "../../notification/app-toast";
import {
  ActiveSidebarIconContainer,
  ActiveSidebarMenuText,
  SidebarIconContainer,
  SidebarMenuContent,
  SidebarMenuItem,
  SidebarMenuText,
} from "./sidebar.styles";

const permission = [
  ENUM_USER_ROLE.AMBASSADOR,
  ENUM_USER_ROLE.VENDOR,
  ENUM_USER_ROLE.OPPORTUNITY_VENDOR,
  ENUM_USER_ROLE.DELEGATE_VENDOR,
];

const defaultDisabled = [ENUM_USER_ROLE.OPPORTUNITY_VENDOR];

const LOCKED_SECTION_TOAST_MESSAGE =
  "Your profile will be fully unlocked once you complete all required profile sections and finalize your profile setup.";

export const MENU_ITEMS = {
  menu: [
    {
      title: "Meetings",
      icon: <Notepad size="24" />,
      path: ROUTE_PATHS.PROJECT.LIST,
      root: "/project",
      permission,
      defaultDisabled,
    },
    {
      title: "Schedule",
      icon: <CalendarDots size="24" />,
      path: ROUTE_PATHS.SCHEDULE,
      permission: [ENUM_USER_ROLE.VENDOR, ENUM_USER_ROLE.OPPORTUNITY_VENDOR, ENUM_USER_ROLE.DELEGATE_VENDOR],
      defaultDisabled,
    },
    {
      title: "Recordings",
      icon: <VideoCamera size="24" />,
      path: ROUTE_PATHS.MEETING_RECORDINGS.LIST,
      root: "/meeting-recordings",
      permission: [ENUM_USER_ROLE.VENDOR, ENUM_USER_ROLE.OPPORTUNITY_VENDOR, ENUM_USER_ROLE.AMBASSADOR],
      defaultDisabled,
    },
    {
      title: "Profile",
      icon: <UserIcon size="24" />,
      path: ROUTE_PATHS.MY_PROFILE,
      defaultDisabled: [],
      permission: [ENUM_USER_ROLE.VENDOR, ENUM_USER_ROLE.OPPORTUNITY_VENDOR, ENUM_USER_ROLE.AMBASSADOR],
    },
    {
      title: "Active Initiatives",
      icon: <ListBullets size="24" />,
      path: ROUTE_PATHS.INITIATIVES.LIST,
      defaultDisabled: [],
      permission: [ENUM_USER_ROLE.AMBASSADOR],
    },
  ],
  general: [
    {
      title: "Settings",
      icon: <GearSix size="24" />,
      path: ROUTE_PATHS.SETTINGS,
      permission: [
        ENUM_USER_ROLE.AMBASSADOR,
        ENUM_USER_ROLE.VENDOR,
        ENUM_USER_ROLE.OPPORTUNITY_VENDOR,
      ],
      defaultDisabled,
    },
  ],
};

const isDrawerVariant = (variant: string | undefined) => variant === "drawer";

const MenuList: any = ({ variant }: { variant?: "default" | "drawer" }) => {
  const userData = useAppSelector((state) => state.auth.user);
  const profileData = useAppSelector((state) => state.profile.data);
  const menuItems = Object.keys(MENU_ITEMS);
  const currentRoutePath = useAppSelector(
    (state: any) => state.router.location.pathname
  );
  const navigate = useNavigate();
  const drawer = isDrawerVariant(variant);
  const isVendorWithoutLicense =
    userData?.role === ENUM_USER_ROLE.VENDOR && !profileData?.licenseForMeeting;

  const _handleMenuItemClick = (innerItem) => {
    const isRoleDisabled = innerItem?.defaultDisabled?.includes(userData?.role);
    const isLicenseLocked =
      isVendorWithoutLicense && innerItem.title !== "Profile";

    if (isRoleDisabled) {
      return;
    }

    if (isLicenseLocked) {
      appToast.info(LOCKED_SECTION_TOAST_MESSAGE);
      return;
    }

    navigate(innerItem.path);
  };

  const _getActive = (innerItem) => {
    return (
      innerItem.path === currentRoutePath ||
      (innerItem.root && currentRoutePath.search(innerItem.root) !== -1)
    );
  };

  return menuItems.map((item, index) => {
    return (
      <React.Fragment key={`menuItem-${index}`}>
        {MENU_ITEMS[item]
          .filter((innerItem) =>
            innerItem?.permission?.includes(userData?.role)
          )
          .map((innerItem, innerIndex) => {
            const isActive = _getActive(innerItem);
            const IconContainer = isActive
              ? ActiveSidebarIconContainer
              : SidebarIconContainer;
            const MenuText = isActive ? ActiveSidebarMenuText : SidebarMenuText;
            const isRoleDisabled = innerItem?.defaultDisabled?.includes(
              userData?.role
            );
            const isLicenseLocked =
              isVendorWithoutLicense && innerItem.title !== "Profile";
            const isLocked = isRoleDisabled || isLicenseLocked;

            return (
              <ButtonMotion
                disabled={isRoleDisabled}
                key={`menuInnerItem-${innerIndex}`}
              >
                <SidebarMenuItem
                  onClick={() => _handleMenuItemClick(innerItem)}
                  disableRipple
                  disableTouchRipple
                  disabled={isRoleDisabled}
                  sx={{
                    ...(isLocked
                      ? {
                          opacity: 0.5,
                          cursor: isLicenseLocked ? "pointer" : "not-allowed",
                        }
                      : undefined),
                    ...(drawer
                      ? {
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          px: 2,
                          py: 1.5,
                        }
                      : undefined),
                  }}
                >
                  <SidebarMenuContent
                    sx={
                      drawer
                        ? {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                        }
                        : undefined
                    }
                  >
                    <IconContainer>{innerItem.icon}</IconContainer>
                    <MenuText
                      variant="small"
                      sx={drawer ? { width: "auto", textAlign: "left" } : undefined}
                    >
                      {innerItem.title}
                    </MenuText>
                  </SidebarMenuContent>
                </SidebarMenuItem>
              </ButtonMotion>
            );
          })}
      </React.Fragment>
    );
  });
};

export { MenuList };
